import { htmlToPlainText as sharedHtmlToPlainText } from '@wit-support/contracts';
/**
 * Tokens the layout renderer knows about. The server calls
 * `renderEmailLayout(layoutHtml, ctx)` with this set and a few runtime-only
 * fields (subject, body, preheader, year, agent.signature). The editor UI
 * enumerates these so admins know what they can reference.
 */
export const LAYOUT_TOKENS = [
    { token: '{{subject}}', description: 'Final subject line of the email' },
    { token: '{{preheader}}', description: 'Inbox preview snippet (hidden in body)' },
    { token: '{{body}}', description: 'The agent-composed reply body' },
    { token: '{{agent.name}}', description: "Signed-in agent's display name" },
    { token: '{{agent.signature}}', description: "Signed-in agent's HTML signature block" },
    { token: '{{contact.name}}', description: "Recipient's display name" },
    { token: '{{year}}', description: 'Current year, for the copyright line' },
];
/**
 * Render the layout HTML against a context. Reuses the same `{{dotted.token}}`
 * regex as `interpolateTemplateBody` but with a broader token set. Unknown
 * tokens are left untouched so drift is visible.
 *
 * Pure function — exported here so both the server (at send time) and the
 * layout editor's preview pane can call it.
 */
export const renderEmailLayout = (layoutHtml, ctx) => {
    return layoutHtml.replace(/\{\{\s*([a-z]+(?:\.[a-z]+)?)\s*\}\}/gi, (match, token) => {
        const key = token.toLowerCase();
        switch (key) {
            case 'subject':
                return escapeHtml(ctx.subject ?? '');
            case 'preheader':
                return escapeHtml(ctx.preheader ?? '');
            case 'body':
                return ctx.body ?? '';
            case 'year':
                return ctx.year;
            case 'agent.name':
                return escapeHtml(ctx.agent?.name?.trim() || 'Support');
            case 'agent.signature':
                return ctx.agent?.signature ?? '';
            case 'contact.name':
                return escapeHtml(ctx.contact?.name?.trim() || 'there');
            default:
                return match;
        }
    });
};
const escapeHtml = (s) => s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
/**
 * Wrap an agent's body in the branded email layout. Plain-text and
 * template-HTML paths converge here so the recipient (and the composer's
 * Preview tab) see exactly the same output.
 *
 * Steps:
 *   1. `toInnerHtml` — if input has no tags, treat as plain text: split on
 *      blank lines → `<p>` per paragraph, `\n` → `<br/>`, HTML-escape.
 *   2. `stripLegacyShell` — peel the old n8n `<div style="max-width:600px…">`
 *      wrapper so imported templates aren't double-wrapped.
 *   3. Build a preheader from the first 90 chars of stripped body.
 *   4. `renderEmailLayout(layout.html, ctx)` substitutes `{{body}}`,
 *      `{{subject}}`, `{{agent.signature}}`, etc.
 *   5. Derive plain-text fallback via `htmlToPlainText`.
 *
 * Pure — no I/O.
 */
export function composeOutbound(input) {
    const { layout, agent, contact, subject } = input;
    const inner = stripLegacyShell(toInnerHtml(input.bodyHtml));
    const preheader = firstChars(stripTagsForPreheader(inner), 90);
    const signature = agent.signatureHtml?.trim() || defaultSignatureHtml();
    const html = renderEmailLayout(layout.html, {
        subject,
        preheader,
        body: inner,
        year: String(new Date().getUTCFullYear()),
        agent: { name: agent.displayName, signature },
        contact: { name: contact.displayName },
    });
    return { subject, html, text: htmlToPlainText(html) };
}
export function toInnerHtml(raw) {
    const trimmed = raw.trim();
    if (!trimmed)
        return '';
    if (/<[a-z!/][^>]*>/i.test(trimmed))
        return trimmed;
    const paragraphs = trimmed
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean);
    return paragraphs
        .map((p) => {
        const escaped = escapeHtml(p).replace(/\n/g, '<br />');
        return `<p style="margin:0 0 16px 0;">${escaped}</p>`;
    })
        .join('\n');
}
export function stripLegacyShell(html) {
    const trimmed = html.trim();
    const openMatch = trimmed.match(/^<div\b[^>]*max-width\s*:\s*600px[^>]*>/i);
    if (!openMatch || !trimmed.endsWith('</div>'))
        return html;
    // Walk the `<div>` tag stream from just past the opener. If any intermediate
    // `</div>` would close the wrapper before the trailing one, we're looking
    // at siblings after the wrapper and must return the original. The old
    // greedy regex spanned across siblings, lifting content that wasn't inside
    // the wrapper (flagged by the audit subagent).
    const inner = trimmed.slice(openMatch[0].length, -'</div>'.length);
    let depth = 1;
    const tagRe = /<\/?div\b[^>]*>/gi;
    let m;
    while ((m = tagRe.exec(inner)) !== null) {
        depth += m[0].startsWith('</') ? -1 : 1;
        if (depth === 0)
            return html;
    }
    return inner.trim();
}
function defaultSignatureHtml() {
    return `<p style="margin:0;font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#6b7280;">Warm regards,<br/>WIT LAB Team</p>`;
}
function stripTagsForPreheader(html) {
    return html
        .replace(/<\s*br\s*\/?\s*>/gi, ' ')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim();
}
function firstChars(s, n) {
    return s.length <= n ? s : s.slice(0, n - 1).trimEnd() + '…';
}
export function htmlToPlainText(html) {
    return sharedHtmlToPlainText(html, 'email');
}
//# sourceMappingURL=email-layout.js.map